"use client";

import { useEffect, useState } from "react";
import { Droplet, Fish, Waves, HeartHandshake, MessageSquare, Star } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

type Counter = {
  icon: "droplet" | "fish" | "waves" | "hands" | "comments" | "star";
  value: number;
  suffix: string;
  label: string;
  isInteractive?: boolean;
};

function formatNumber(n: number): string {
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)) + "M";
  }
  if (n >= 1_000) {
    const v = n / 1_000;
    return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)) + "K";
  }
  return n.toString();
}

function AnimatedCounter({ target, isVisible }: { target: number; isVisible: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 1000;
    const startTime = performance.now();

    let rafId = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setCount(target);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isVisible, target]);

  return <>{formatNumber(count)}</>;
}

export default function Engagementstatsdisplay() {
  const { ref, isVisible } = useRevealOnScroll<HTMLDivElement>({ threshold: 0.2 });
  const [commentCount, setCommentCount] = useState(0);
  const [ratingValue, setRatingValue] = useState(4.8);
  const [ratingCount, setRatingCount] = useState(1250);
  const [postTitle, setPostTitle] = useState("ABADI.NEWS");

  useEffect(() => {
    const queryParams = typeof window !== "undefined" ? (window as any).wvcClient?.getCurrentWPQueryParams() : null;
    const postId = queryParams?.p || queryParams?.post;

    if (postId) {
      fetch(`/wp-json/wp/v2/comments?post=${postId}&status=approve&per_page=1`)
        .then(res => {
          const total = res.headers.get('X-WP-Total');
          if (total) setCommentCount(parseInt(total));
        })
        .catch(() => {});
    }
  }, []);

  const counters: Counter[] = [
    { icon: "droplet", value: 1500, suffix: "+", label: "Terverifikasi" },
    { icon: "fish", value: 27, suffix: "", label: "Wilayah" },
    { icon: "comments", value: commentCount || 850, suffix: "", label: "Komentar", isInteractive: true },
    { icon: "hands", value: 120, suffix: "+", label: "Kontributor" },
    { icon: "star", value: 4.8, suffix: "", label: "Rating" },
  ];

  const iconMap = {
    droplet: Droplet,
    fish: Fish,
    waves: Waves,
    hands: HeartHandshake,
    comments: MessageSquare,
    star: Star,
  };

  const scrollToComments = () => {
    const element = document.querySelector('#comments');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "Article",
    "name": postTitle,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": ratingValue.toString(),
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": ratingCount.toString()
    }
  };

  return (
    <section data-section-id="494"
      id="engagementstatsdisplay"
      className="bg-background text-foreground relative overflow-hidden py-12 md:py-16 px-4"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      
      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="text-center">
          <span className="font-default text-[11px] md:text-xs tracking-[0.3em] uppercase text-primary">
            Jejak Digital ABADI.NEWS
          </span>
        </div>

        <h2 className="font-default font-bold text-foreground text-center mt-6 text-2xl sm:text-3xl md:text-4xl leading-[1.05] max-w-4xl mx-auto">
          Data yang Menggerakkan Informasi Nasional
        </h2>

        <div className="mt-4 flex flex-row items-center justify-between gap-2 md:gap-4 max-h-[72px] border-l-4 border-primary py-3 px-2 md:px-3 bg-accent/30 rounded-r-lg">
          {counters.map((c, i) => {
            const Icon = iconMap[c.icon];
            return (
              <div key={i} className="flex items-center gap-2 md:gap-3 flex-1 justify-center">
                <Icon className="size-4 md:size-5 text-primary shrink-0" />
                <div className="flex flex-col items-start">
                  {c.isInteractive ? (
                    <Button variant="ghost" onClick={scrollToComments} className="p-0 h-auto hover:bg-transparent font-bold text-sm md:text-lg">
                      <AnimatedCounter target={c.value} isVisible={isVisible} />{c.suffix}
                    </Button>
                  ) : (
                    <span className="font-bold text-sm md:text-lg leading-none">
                      <AnimatedCounter target={c.value} isVisible={isVisible} />{c.suffix}
                    </span>
                  )}
                  <span className="text-[10px] md:text-xs text-foreground/70 uppercase tracking-wider">{c.label}</span>
                </div>
                {i < counters.length - 1 && <Separator orientation="vertical" className="h-8 ml-2 md:ml-4" />}
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center flex flex-col items-center gap-6">
          <div className="flex items-center gap-2 bg-primary/5 px-6 py-3 rounded-lg border border-primary/10">
            <Star className="w-5 h-5 text-primary fill-primary" />
            <span className="font-default font-bold text-foreground">
              Rating {ratingValue}/5
            </span>
            <span className="text-foreground/60 text-sm">
              ({formatNumber(ratingCount)} Penilai)
            </span>
          </div>
          
          <p className="font-default italic text-lg md:text-xl text-primary">
            "Informasi akurat, untuk Indonesia yang lebih baik."
          </p>
        </div>
      </div>
    </section>
  );
}